import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback
} from 'react';
import {
  Button,
  ButtonType,
  Drawer,
  Input,
  Checkbox,
  Table
} from 'syngenta-digital-cropwise-react-ui-kit';
import LoadingSpinner from 'components/LoadingSpinner';
import EmptyTableState from 'components/EmptyTableState';
import useTablePaginationAndSort from 'hooks/useTablePaginationAndSort';
import CloseIcon from 'assets/close.svg?react';
import SearchIcon from 'assets/search.svg?react';
import PestIcon from 'assets/pest-icon.svg?react';
import IllustrationSmallEmpty from 'assets/Illustration Small Empty.svg';
import useDebounce from 'hooks/useDebounce';
import usePestsDataV2 from 'hooks/usePestsDataV2';
import { PestOption } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import BUTTON_TEXTS, {
  TABLE_SCROLL_HEIGHT,
  MIN_SEARCH_LENGTH,
  DRAWER_WIDTH,
  EMPTY_STATE_MESSAGES,
  PLACEHOLDER_TEXT
} from './constants';
import {
  createPersistentPestOptions,
  createSearchPestOptions,
  getIdsForSelectedPests,
  areArraysDifferent,
  isPestInPersistentList,
  shouldKeepPersistentPest,
  focusSearchInput
} from './helpers/pestSelectionHelpers';

import './style.css';

// Separate component for pagination total display
const PaginationTotal: React.FC<{
  total: number;
  range: [number, number] | undefined;
}> = ({ total, range }) => {
  // Safety check for range parameter
  if (!range || !Array.isArray(range) || range.length < 2) {
    return (
      <span className="text-sm font-normal leading-5 tracking-tight text-neutral-60">
        0 results
      </span>
    );
  }

  return (
    <span className="text-sm font-normal leading-5 tracking-tight text-neutral-60">
      {range[0]}-{range[1]} of {total} results
    </span>
  );
};

// Function for showTotal that Ant Design pagination expects
const showTotalFunction = (total: number, range: [number, number]) => {
  return <PaginationTotal total={total} range={range} />;
};

interface TargetPestSelectionDrawerProps {
  visible: boolean;
  zIndex?: number;
  selectedPest?: PestOption[];
  onClose: () => void;
  onSave: (pestObjects: PestOption[]) => void;
}

const DEFAULT_PAGE_SIZE = 50;

const TargetPestSelectionDrawer: React.FC<TargetPestSelectionDrawerProps> = ({
  visible,
  zIndex = 998,
  selectedPest = [],
  onClose,
  onSave
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [tempSelectedPests, setTempSelectedPests] = useState<PestOption[]>(
    () => {
      return selectedPest || [];
    }
  );
  const [persistentPests, setPersistentPests] = useState<PestOption[]>([]);
  const [tempSelectedPestIds, setTempSelectedPestIds] = useState<string[]>([]);
  const [initialSelectedPests, setInitialSelectedPests] = useState<
    PestOption[]
  >(() => {
    return selectedPest || [];
  });
  const searchInputRef = useRef<any>(null);
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  // FIX: Use ref to track selectedPest changes without causing loops
  const selectedPestRef = useRef(selectedPest);
  useEffect(() => {
    selectedPestRef.current = selectedPest;
  }, [selectedPest]);

  const resetToOriginalState = useCallback(() => {
    const pestArray = selectedPestRef.current || [];
    setTempSelectedPests(pestArray);
    setPersistentPests(pestArray);
    setInitialSelectedPests(pestArray);
    // Reset the IDs to match the original selected pests
    setTempSelectedPestIds(pestArray.map(pest => pest.id));
  }, []); // FIX: Empty dependency array - use ref for selectedPest

  // Initialize pagination and sorting
  const paginationAndSort = useTablePaginationAndSort({
    pageSize: DEFAULT_PAGE_SIZE,
    defaultSortBy: 'name',
    defaultSortDir: 'ascend'
  });

  const {
    reactUiKitTableOnChange,
    reactUiKitTablePaginationProp,
    setTotalItemsCount,
    setPage
  } = paginationAndSort as any;

  // Update pagination position to top
  const topPaginationProp = {
    ...reactUiKitTablePaginationProp,
    position: ['topRight'],
    showTotal: showTotalFunction,
    showLessItems: true
  };

  const debouncedSearch = useDebounce(searchValue, 200);
  const { pests, totalCount, loading } = usePestsDataV2(
    searchValue ? debouncedSearch : '',
    { limit: DEFAULT_PAGE_SIZE, page: topPaginationProp.current - 1 }
  );

  const pestOptions: PestOption[] = useMemo(() => {
    // If we have a search term with MIN_SEARCH_LENGTH+ characters, show search results
    if (searchValue.trim() && searchValue.length >= MIN_SEARCH_LENGTH) {
      return createSearchPestOptions(pests);
    }
    // If no search or search is less than MIN_SEARCH_LENGTH characters, show persistent pests
    // This includes when search is cleared, persistent pests should reappear
    return createPersistentPestOptions(persistentPests);
  }, [pests, searchValue, persistentPests]);

  // Pagination logic
  const totalPests = totalCount;

  // Update total items count when pestOptions changes
  useEffect(() => {
    setTotalItemsCount(totalPests);
  }, [totalPests, setTotalItemsCount]);

  // Reset pagination to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, setPage]);

  // Sync tempSelectedPestIds with tempSelectedPests when pestOptions change
  // FIX: Use ref to prevent infinite loops
  const tempSelectedPestsRef = useRef(tempSelectedPests);
  tempSelectedPestsRef.current = tempSelectedPests;

  useEffect(() => {
    const idsForSelectedPests = getIdsForSelectedPests(
      pestOptions,
      tempSelectedPestsRef.current
    );

    if (idsForSelectedPests.length > 0) {
      setTempSelectedPestIds(prev => {
        if (areArraysDifferent(prev, idsForSelectedPests)) {
          return idsForSelectedPests;
        }
        return prev;
      });
    } else {
      // Clear pest IDs when no pests are selected
      setTempSelectedPestIds(prev => (prev.length > 0 ? [] : prev));
    }
  }, [pestOptions, tempSelectedPestIds]);

  // Clean up unchecked persistent pests when starting a search
  // FIX: Use debounced search to prevent rapid updates
  useEffect(() => {
    if (searchValue.trim() && searchValue.length >= MIN_SEARCH_LENGTH) {
      // Remove unchecked items from persistent pests when searching
      setPersistentPests(prevPersistent =>
        prevPersistent.filter(persistent =>
          shouldKeepPersistentPest(persistent, tempSelectedPestsRef.current)
        )
      );
    }
  }, [searchValue]);

  // Sync tempSelectedPests with selectedPest prop when drawer opens
  // FIX: Remove resetToOriginalState dependency to prevent loops
  useEffect(() => {
    if (visible) {
      resetToOriginalState();
    }
  }, [visible, resetToOriginalState]); // Removed selectedPest dependency

  // FIX: Separate effect to handle selectedPest changes when drawer is open
  useEffect(() => {
    if (visible && selectedPest !== selectedPestRef.current) {
      resetToOriginalState();
    }
  }, [visible, selectedPest, resetToOriginalState]);

  // Auto-focus search input when drawer opens
  useEffect(() => {
    if (visible) {
      focusTimerRef.current = focusSearchInput(searchInputRef, 200);
    }
    return () => {
      if (focusTimerRef.current) {
        clearTimeout(focusTimerRef.current);
      }
    };
  }, [visible]);

  // Check if there are changes compared to initial state
  const hasChanges = useMemo(() => {
    if (tempSelectedPests.length !== initialSelectedPests.length) {
      return true;
    }

    // Sort both arrays by ID to compare regardless of order
    const sortedCurrent = [...tempSelectedPests].sort((a, b) =>
      a.id.localeCompare(b.id)
    );
    const sortedInitial = [...initialSelectedPests].sort((a, b) =>
      a.id.localeCompare(b.id)
    );

    return !sortedCurrent.every(
      (pest, index) => pest.id === sortedInitial[index]?.id
    );
  }, [tempSelectedPests, initialSelectedPests]);

  // Helper function to add pest to persistent list if not already there
  const addToPersistentPests = useCallback((pest: PestOption) => {
    setPersistentPests(prevPersistent => {
      if (isPestInPersistentList(pest, prevPersistent)) {
        return prevPersistent;
      }
      return [...prevPersistent, pest];
    });
  }, []);

  const handleSelectPest = useCallback(
    (pest: PestOption) => {
      const isCurrentlySelected = tempSelectedPestIds.includes(pest.id);

      setTempSelectedPestIds(prev => {
        if (isCurrentlySelected) {
          return prev.filter(id => id !== pest.id);
        }
        return [...prev, pest.id];
      });

      setTempSelectedPests(prev => {
        const isObjectSelected = prev.some(p => p.id === pest.id);

        if (isCurrentlySelected) {
          // Remove the specific pest object from selection
          return prev.filter(p => p.id !== pest.id);
        }

        // Add to selection if not already selected
        if (!isObjectSelected) {
          // Add to persistent pests for visibility
          addToPersistentPests(pest);
          return [...prev, pest];
        }

        return prev;
      });
    },
    [tempSelectedPestIds, addToPersistentPests]
  );

  const handleCancel = useCallback(() => {
    setSearchValue('');
    resetToOriginalState();
    onClose();
  }, [resetToOriginalState, onClose]);

  const handleClearSelection = () => {
    setTempSelectedPests([]);
    setTempSelectedPestIds([]);
    setPersistentPests([]); // Clear persistent pests as well
    setSearchValue(''); // Also clear the search input

    // Focus the search input after clearing (cancel any pending focus first)
    if (focusTimerRef.current) {
      clearTimeout(focusTimerRef.current);
    }
    focusTimerRef.current = focusSearchInput(searchInputRef, 100);
  };

  const handleSave = () => {
    setSearchValue(''); // Clear search input on save
    onSave(tempSelectedPests);
    onClose();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  // Table columns configuration
  const columns = [
    {
      title: 'Pest Name',
      dataIndex: 'name',
      key: 'name',
      render: (_: any, pest: PestOption) => (
        <div
          className="flex items-center gap-3 w-full"
          style={{ height: '20px' }}
        >
          <Checkbox
            checked={tempSelectedPestIds.includes(pest.id)}
            onChange={() => handleSelectPest(pest)}
          />
          <div className="flex-1">
            <p className="font-medium text-neutral-90">{pest.name}</p>
          </div>
        </div>
      ),
      onCell: (pest: PestOption) => ({
        onClick: (e: React.MouseEvent) => {
          // Only handle clicks if not clicking directly on the checkbox
          if ((e.target as HTMLElement).tagName !== 'INPUT') {
            handleSelectPest(pest);
          }
        },
        style: { cursor: 'pointer' }
      })
    }
  ];

  const renderEmptyState = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8 text-neutral-60">
          <LoadingSpinner message="Loading..." />
        </div>
      );
    }

    // Show empty state when no results found after searching
    if (searchValue.trim().length >= MIN_SEARCH_LENGTH && totalPests === 0) {
      return (
        <EmptyTableState
          title=""
          message={EMPTY_STATE_MESSAGES.NO_RESULTS}
          overlayIcon={IllustrationSmallEmpty}
          containerClassName="w-full h-full pt-2 pb-16 flex flex-col justify-center items-center"
          messageClassName="text-center text-sm font-normal leading-5 tracking-tight mx-auto text-black empty-state-message-204"
        />
      );
    }

    // Show search instruction when no search and no persistent pests, or when searching has less than 3 characters
    if (
      searchValue.trim().length <= MIN_SEARCH_LENGTH &&
      tempSelectedPests.length === 0
    ) {
      return (
        <EmptyTableState
          title=""
          message={EMPTY_STATE_MESSAGES.SEARCH_INSTRUCTION}
          overlayIcon={IllustrationSmallEmpty}
          containerClassName="w-full h-full pt-2 pb-16 flex flex-col justify-center items-center"
          messageClassName="text-center text-sm font-normal leading-5 tracking-tight mx-auto text-black empty-state-message-204"
        />
      );
    }

    return null;
  };

  return (
    <Drawer
      zIndex={zIndex}
      className="product-drawer target-pest-drawer"
      open={visible}
      placement="right"
      closable={false}
      maskClosable={false}
      onClose={onClose}
      width={DRAWER_WIDTH}
      footer={
        <div className="flex justify-between items-center">
          <Button type={ButtonType.outline} onClick={handleCancel} size="large">
            {BUTTON_TEXTS.CANCEL}
          </Button>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <PestIcon />
              <span
                className="text-sm font-normal leading-5"
                style={{
                  letterSpacing: '-0.105px',
                  color: '#0071CD'
                }}
              >
                {tempSelectedPests.length}{' '}
                {tempSelectedPests.length === 1 ? 'pest' : 'pests'} selected
              </span>
            </div>

            <div className="flex gap-3">
              <Button
                type={ButtonType.primary}
                ghost
                onClick={handleClearSelection}
                size="large"
                disabled={
                  tempSelectedPests.length === 0 && persistentPests.length === 0
                }
              >
                {BUTTON_TEXTS.CLEAR}
              </Button>
              <Button
                type={ButtonType.primary}
                onClick={handleSave}
                size="large"
                disabled={!hasChanges}
              >
                {BUTTON_TEXTS.SAVE_PESTS}
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <div className="relative">
        <div className="absolute top-0 right-0 m-3">
          <Button
            type={ButtonType.link}
            aria-label="Close target pest selection"
            onClick={onClose}
            icon={<CloseIcon aria-hidden="true" />}
          />
        </div>
      </div>

      <div className="px-8 pt-8">
        <div className="mb-4">
          <h1 className="text-xl font-semibold mb-2">Target Pest</h1>
        </div>

        <div className="mb-5">
          <div className="relative">
            <Input
              ref={searchInputRef}
              id="pest-search"
              name="pest-search"
              value={searchValue}
              onChange={handleSearchChange}
              placeholder={PLACEHOLDER_TEXT}
              prefix={<SearchIcon className="w-4 h-4 text-neutral-40" />}
              allowClear
              autoComplete="off"
            />
          </div>
        </div>
      </div>

      {/* Results count - Show when displaying persistent items (NOT during search) */}
      {!loading &&
        pestOptions.length > 0 &&
        (!searchValue.trim() || searchValue.length < MIN_SEARCH_LENGTH) &&
        persistentPests.length > 0 && (
          <div className="px-8 pb-3">
            <div className="flex justify-end items-center">
              <span className="text-sm font-normal leading-5 tracking-tight text-neutral-60">
                {`${persistentPests.length} ${
                  persistentPests.length === 1 ? 'result' : 'results'
                }`}
              </span>
            </div>
          </div>
        )}

      {/* Zero results message - Only show when no results and no persistent items */}
      {!loading &&
        ((searchValue.length < MIN_SEARCH_LENGTH &&
          persistentPests.length === 0) ||
          (searchValue.length >= MIN_SEARCH_LENGTH && totalPests === 0)) && (
          <div className="px-8 pb-4">
            <div className="flex justify-end items-center">
              <span className="text-sm font-normal leading-5 tracking-tight text-neutral-60">
                0 results
              </span>
            </div>
          </div>
        )}

      {/* Table - Full width without padding */}
      <div>
        <Table
          columns={columns}
          dataSource={loading ? [] : pestOptions}
          pagination={
            pestOptions.length > 0 &&
            searchValue.trim() &&
            searchValue.length >= MIN_SEARCH_LENGTH
              ? topPaginationProp
              : false
          }
          onChange={reactUiKitTableOnChange}
          scroll={{ y: TABLE_SCROLL_HEIGHT }}
          rowKey={record => `pest-${record.pestId}-${record.nameId}`}
          locale={{
            emptyText: <div className="px-8">{renderEmptyState()}</div>
          }}
        />
      </div>
    </Drawer>
  );
};

export default TargetPestSelectionDrawer;
